import dayjs, { Dayjs } from 'dayjs'
import { useCallback, useMemo } from 'react'
import { useAppSelector } from 'store/useStoreHook'
import currencyFormat, {
    ICurrencyFormatOption,
    currencyMillifyFormat,
} from './currencyFormat'

export function useFormat() {
    const cCode = useAppSelector((state) => state.operation.data.currencyCode)

    const cSymbol = useAppSelector(
        (state) => state.operation.data.currencySymbol,
    )
    const userData = useAppSelector((state) => state.auth.user)
    const language = useAppSelector((state) => state.operation.data.language)
    const currentLang = useMemo(() => {
        return userData?.userPreference?.language ?? (language || 'en')
    }, [language, userData?.userPreference?.language])

    const currencyCode = useMemo(() => cCode || 'USD', [cCode])
    const currencySymbol = useMemo(() => cSymbol || '$', [cSymbol])

    const dayDateTimeShort = useCallback(
        (date?: Dayjs | string, hasOffset?: boolean) => {
            if (!date) date = dayjs()
            if (typeof date === 'string') date = dayjs(date)
            return date.format('lll ' + (hasOffset ? 'Z' : ''))
        },
        [],
    )
    const dateImpl = useCallback(
        (date?: Dayjs | string, hasOffset?: boolean) => {
            if (!date) date = dayjs()
            if (typeof date === 'string') date = dayjs(date)
            return date.format('llll ' + (hasOffset ? 'Z' : ''))
        },
        [],
    )
    const customDateTimeFormat = useCallback(
        (date: Dayjs | string, format?: string) => {
            if (typeof date === 'string') date = dayjs(date)
            return date.format(format || 'LLLL')
        },
        [],
    )
    const humanDateFormat = useCallback((date?: Dayjs | string) => {
        if (!date) return ''
        else if (typeof date === 'string') date = dayjs(date)
        return date.format('MMMM D, YYYY')
    }, [])

    const shortDateFormat = useCallback((date?: Dayjs | string) => {
        if (!date) return ''
        else if (typeof date === 'string') date = dayjs(date)
        return date.format('L')
    }, [])
    const formatPeriodHour = (utcDate?: string | Date) => {
        if (!utcDate) {
            return ''
        }

        const localDate = new Date(
            typeof utcDate === 'string' ? utcDate + 'Z' : utcDate,
        )

        if (isNaN(localDate.getTime())) {
            return ''
        }

        const year = localDate.getFullYear()
        const month = (localDate.getMonth() + 1).toString().padStart(2, '0')
        const day = localDate.getDate().toString().padStart(2, '0')
        const hour = localDate.getHours().toString().padStart(2, '0')

        return `${year}/${month}/${day} ${hour}:00-${hour}:59`
    }
    const timeFormat = useCallback(
        (
            date?: Dayjs | string,
            isAM: boolean = true,
            includeSeconds: boolean = false,
            setMinute?: number,
        ) => {
            if (!date) return ''
            else if (typeof date === 'string') date = dayjs(date)

            if (setMinute !== undefined) {
                date = date.set('minute', setMinute)
            }

            if (isAM) {
                return date.format(includeSeconds ? 'LTS' : 'LT')
            } else {
                return date.format(includeSeconds ? 'HH:mm:ss' : 'HH:mm')
            }
        },
        [],
    )

    const timeOneHour = useCallback(
        (
            dateTime?: Dayjs | string,
            isAM: boolean = true,
            includeSeconds: boolean = false,
        ) => {
            let from = timeFormat(dateTime, isAM, includeSeconds, 0)
            let to = timeFormat(dateTime, isAM, includeSeconds, 59)

            if (!from || !to) {
                return ''
            }

            return from + ' to ' + to
        },
        [timeFormat],
    )

    const timeFormatFromTo = useCallback(
        (
            dateTimeFrom?: Dayjs | string,
            dateTimeTo?: Dayjs | string,
            isAM: boolean = false,
            includeSeconds: boolean = false,
            setMinuteFrom?: number,
            setMinuteTo?: number,
        ) => {
            let from = timeFormat(
                dateTimeFrom,
                isAM,
                includeSeconds,
                setMinuteFrom,
            )
            let to = timeFormat(dateTimeTo, isAM, includeSeconds, setMinuteTo)

            if (!from || !to) {
                return ''
            }

            return from + ' to ' + to
        },
        [timeFormat],
    )

    const dateTimeImpl = useCallback(
        (date?: Dayjs | string, isAM: boolean = true) => {
            if (!date) date = dayjs()
            else if (typeof date === 'string') date = dayjs(date)
            return shortDateFormat(date) + ' ' + timeFormat(date, isAM)
        },
        [shortDateFormat, timeFormat],
    )
    const currencyImpl = useCallback(
        (
            value: number | string,
            otherCurrencyCode: string | undefined = undefined,
            options: ICurrencyFormatOption = {
                fraction: true,
                isAccounting: false,
            },
        ) => {
            return currencyFormat(
                value,
                currentLang,
                otherCurrencyCode || currencyCode,
                options,
            )
        },
        [currentLang, currencyCode],
    )

    const decimalFormat = useCallback(
        (
            value: number | string,
            fraction: boolean = true,
            isAccounting: boolean = false,
        ) => {
            if (typeof value === 'string') value = parseFloat(value)

            return new Intl.NumberFormat(currentLang, {
                minimumFractionDigits: fraction ? 2 : undefined,
                maximumFractionDigits: fraction ? 2 : undefined,
                style: 'decimal',
                currencyDisplay: 'narrowSymbol',
                currencySign: isAccounting ? 'accounting' : 'standard',
                signDisplay: value === 0 ? 'never' : 'auto',
            }).format(value)
        },
        [currentLang],
    )

    const pickupDateTimeFormat = useCallback(
        (isAsap: boolean, pickupDateTime: string | dayjs.Dayjs) => {
            const today = dayjs().startOf('day')
            const dayJsPickupDT = dayjs(pickupDateTime)
            const isToday = dayJsPickupDT.isSame(today, 'day')

            if (isAsap)
                return isToday
                    ? `Today (ASAP)`
                    : `${dayJsPickupDT.format('LL')} (ASAP)`
            else
                return isToday
                    ? `Today ${dayJsPickupDT.format('h:mm A')}`
                    : dayJsPickupDT.format('LLLL')
        },
        [],
    )

    const inventoryQuantityFormat = useCallback(
        (value: number | undefined = 0) => {
            return new Intl.NumberFormat(currentLang, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 5,
                style: 'decimal',
                currencyDisplay: 'narrowSymbol',
                signDisplay: value === 0 ? 'never' : 'auto',
            }).format(value)
        },
        [currentLang],
    )
    const millifyCurrency = useCallback(
        (
            value: number | string,
            options: ICurrencyFormatOption = {
                fraction: true,
                isAccounting: false,
            },
        ) => {
            return currencyMillifyFormat(
                value || 0,
                currentLang,
                currencyCode,
                options,
            )
        },
        [currencyCode, currentLang],
    )

    const dayOfWeekFormat = useCallback((date?: Dayjs | string) => {
        if (!date) return ''
        else if (typeof date === 'string') date = dayjs(date)
        return date.format('dddd') // 'dddd' for full name of the day
    }, [])

    const displayPercentDiff = (before: any, after: any) => {
        return displayPercent(
            (((after ?? 0) - (before ?? 0)) / (before ?? 1)) * 100,
        )
    }

    const displayPercent = (value: any) => {
        if (value == null) return ''
        return value.toFixed(2) + '%'
    }

    const weekRangeFormat = useCallback((date?: Dayjs | string) => {
        if (!date) return ''

        if (typeof date === 'string') date = dayjs(date)

        const startOfWeek = date.startOf('week')
        const endOfWeek = startOfWeek.add(6, 'day')

        const start = startOfWeek.format('DD')
        const end = endOfWeek.format('DD/MM/YYYY')

        return `${start}-${end}`
    }, [])

    const yearMonthFormat = useCallback(
        (date?: Dayjs | string, fullMonthName: boolean = false) => {
            if (!date) return ''

            if (typeof date === 'string') date = dayjs(date)

            return fullMonthName
                ? date.format('MMMM YYYY') // เดือนเต็มและปี
                : date.format('YYYY-MM') // ปีและเดือนในรูปแบบ YYYY-MM
        },
        [],
    )
    const timeAndDateLong = useCallback((date?: string): string => {
        const today = dayjs()
        const compareDate = dayjs(date)
        if (today.isSame(compareDate, 'day')) {
            return (
                compareDate.format('HH:mm, ') +
                'Today, ' +
                compareDate.format('D MMMM YYYY')
            )
        }
        return compareDate.format('HH:mm, dddd, D MMMM YYYY')
    }, [])

    return {
        date: dateImpl,
        dateTimeFormat: dateTimeImpl,
        dayDateTimeShort,
        shortDateFormat: shortDateFormat,
        humanDateFormat,
        time: timeFormat,
        currency: currencyImpl,
        currencySymbol,
        currencyCode: currencyCode,
        currencyLanguage: currentLang,
        decimalFormat,
        inventoryQuantityFormat,
        customDateTimeFormat,
        millifyCurrency,
        pickupDateTimeFormat,
        formatPeriodHour,
        timeFormatFromTo,
        timeOneHour,
        dayOfWeekFormat,
        displayPercent,
        displayPercentDiff,
        weekRangeFormat,
        yearMonthFormat,
        timeAndDateLong,
    }
}
